import * as Sentry from '@sentry/vue'
import constants from '@/constant'
import socketHelper from '@/helpers/socket/socket-helper'
import utils from '@/util'
import OpenAIApi from '@/api/openai'
import store from '@/vuex/store'
import i18nPresenter from '@/i18nPresenter'
import SlideApi from '@/api/slide'
import IdeasTabName from '@/constant/IdeasTabName'
import EventCategories from '@/constant/eventCategories'
import { SimpleCache } from '@/utils/cache.utils'

const cache = new SimpleCache()

const updateSlideActiveAction = ({ commit }, payload) => {
  commit('updateSlideActive', payload)
}

const updateSlideByIdAction = ({ commit }, payload) => {
  commit('updateSlideById', payload)
}

const setSlideActiveAction = ({ commit, state }, payload) => {
  payload = payload || {}
  payload.randomIndexId = utils.generateRandomString() // https://ahaslides.atlassian.net/browse/AHA-23989
  payload.SlideOptions = payload.SlideOptions || []

  const slideTypes = [
    'bulletPoints',
    'multipleChoice',
    'pickAnswer',
    'imageChoice',
    'scale',
  ]

  const customDefaultSlideOptionsType = ['matchPairs', 'correctOrder']

  // Create default SlideOptions
  if (slideTypes.includes(payload.type) &&
    !customDefaultSlideOptionsType.includes(payload.slideType) &&
    payload.SlideOptions.length === 0) {
    payload.SlideOptions = [
      { index: 1, order: 1, randomIndexId: utils.generateRandomString() },
      { index: 2, order: 2, randomIndexId: utils.generateRandomString() },
      { index: 3, order: 3, randomIndexId: utils.generateRandomString() },
    ]
  }

  if (payload.type === 'voiceQA' && payload.SlideOptions.length === 0) {
    payload.SlideOptions = [
      {
        index: 1, order: 1, title: 'Your name', randomIndexId: utils.generateRandomString(),
      },
      {
        index: 2, order: 2, title: 'Your sitting area', randomIndexId: utils.generateRandomString(),
      },
    ]
  }

  if (payload.type === 'ideas') {
    payload.limitChoice = Math.min(constants.MAX_VOTE_IDEAS, payload.limitChoice)
    payload.limitChoice = Math.max(constants.MIN_VOTE_IDEAS, payload.limitChoice)
  } else {
    payload.limitChoice = Math.max(2, payload.limitChoice || 2)
  }

  if (payload.type === 'bulletPoints') {
    payload.bulletPoints = payload.bulletPoints || []
  }

  if (state.route.name === 'PresentationDetail' &&
    state.lastSlideActiveId &&
    payload.id &&
    state.lastSlideActiveId !== payload.id
  ) {
    socketHelper.leaveRoomSlide(state.presentation.uniqueAccessCode, state.lastSlideActiveId)
    socketHelper.joinRoomSlide(state.presentation.uniqueAccessCode, payload.id)
  }

  commit('setSlideActive', payload)
}

const checkSlideMovingDirectionAction = ({ commit }, slideActiveId) => {
  commit('checkSlideMovingDirection', slideActiveId)
}

const concatSlideOptionsAction = ({ commit, state }, payload) => {
  const currentSlideOptions = state.slideActive.SlideOptions || []
  const newSlideOptions = payload.slideOptions
  commit('updateSlideById', {
    id: payload.slideId,
    SlideOptions: currentSlideOptions.concat(newSlideOptions),
  })
}

const generateOptionsAction = async ({ commit }, {
  slideId,
  onGenerateOptionsSuccess,
  onGenerateOptionsError,
}) => {
  try {
    const { slide: slideObject } = await OpenAIApi.generateOptions({ slideId })

    if (!slideObject) {
      store.dispatch('setSyncLoadingAction', 3)
      return
    }

    slideObject.otherCorrectQuiz = slideObject.otherCorrectQuizJson || slideObject.otherCorrectQuiz

    await commit('updateSlideById', {
      id: slideId,
      ...slideObject,
    })
    onGenerateOptionsSuccess(slideObject.SlideOptions)
    utils.showToastSuccess('Options have been generated successfully!')
  } catch (error) {
    utils.showToastError(i18nPresenter.t('Something went wrong. Please try again.'))
    onGenerateOptionsError(error)
  }
}

const updateSlideCanvasBlockByIdAction = async ({ commit, state, rootGetters }, payload) => {
  if (!payload.canvasBlocksUrl) return null

  const canUseContentSlideFix = rootGetters['featureToggles/canUseContentSlideFix']

  if (canUseContentSlideFix && state.isBlockHistory) {
    // User is actively editing: defer the KV-sync update so we don't overwrite
    // locally-added or in-progress blocks. Store the latest payload; it will be
    // replayed by flushPendingCanvasBlockAction once the editor blurs (AHA-15513).
    commit('setPendingCanvasBlockPayload', { ...payload })
    return null
  }

  let canvasBlocks
  if (canUseContentSlideFix) {
    canvasBlocks = await fetch(payload.canvasBlocksUrl)
      .then((res) => res.json())
      .then((body) => ({ body }))
      .catch((err) => {
        Sentry.captureException(err)
        return null
      })
  } else {
    canvasBlocks = await fetch(payload.canvasBlocksUrl).then((res) => res.json()).then((body) => ({ body }))
  }
  if (!canvasBlocks || !canvasBlocks.body) return null
  payload.canvasBlocks = canvasBlocks.body
  commit('updateSlideById', payload)
  return null
}

const flushPendingCanvasBlockAction = async ({ commit, state, rootGetters }) => {
  if (!rootGetters['featureToggles/canUseContentSlideFix']) return
  const payload = state.pendingCanvasBlockPayload
  if (!payload) return
  commit('setPendingCanvasBlockPayload', null)

  const canvasBlocks = await fetch(payload.canvasBlocksUrl)
    .then((res) => res.json())
    .then((body) => ({ body }))
    .catch((err) => {
      Sentry.captureException(err)
      return null
    })
  if (!canvasBlocks || !canvasBlocks.body) return
  payload.canvasBlocks = canvasBlocks.body
  commit('updateSlideById', payload)
}

const getSlideAttributesAction = async ({ rootGetters }, slideId) => {
  const canUseSlidePlugin = rootGetters['slidePlugin/canUseSlidePlugin']
  const attributeList = await SlideApi.getAttributesBySlideId({
    slideId,
    ...(canUseSlidePlugin ? { resignImage: true } : {}),
  })
  return (attributeList ?? []).reduce((obj, attr) => {
    obj[attr.type] = attr.attributes
    return obj
  }, {})
}

const slideAttributesCacheKey = (slideId) => `getSlideAttributesAction-${slideId}`

// Bumped from 5s so the host can prefetch the NEXT slide's attributes during
// the current slide and still get a cache hit when the presenter advances (see
// PreloadIframeHost). Two purge paths keep the longer TTL from serving stale
// data: local writes drop the key inline (upsertSlideAttributeAction below),
// and any inbound `slideAttributes/updateAttributes` mutation — i.e. an edit
// arriving over live sync (MQTT kvSync) from another client or a plugin's own
// request — drops it via purgeSlideAttributesCacheAction / the cache-purge
// store plugin.
const SLIDE_ATTRIBUTES_CACHE_MAX_AGE_SECONDS = 60

const getSlideAttributesWithCacheAction = async ({ dispatch }, slideId) => {
  return cache.invoke(() => {
    return dispatch('getSlideAttributesAction', slideId)
  }, {
    key: slideAttributesCacheKey(slideId),
    maxAgeInSeconds: SLIDE_ATTRIBUTES_CACHE_MAX_AGE_SECONDS,
  })
}

const upsertSlideAttributeAction = async (_, { slideId, attributeKey, attributeValue }) => {
  const result = await SlideApi.upsertSlideAttribute(slideId, {
    attributeKey,
    attributeValue,
  })
  // Write-through invalidation: drop the cached bootstrap so the next mount
  // re-reads fresh data instead of serving a stale value within the TTL window.
  cache.purge(slideAttributesCacheKey(slideId))
  return result
}

// Invalidate the cached bootstrap for a single slide. Dispatched by
// slideAttributesCachePurgePlugin on every `slideAttributes/updateAttributes`
// mutation, so an attribute change arriving over live sync (MQTT kvSync) — a
// cross-client edit, or a plugin that persists via its own iframe request
// instead of upsertSlideAttributeAction — drops the stale entry. Without this
// the 60s TTL could keep serving pre-change data to the next mount. No-op when
// the key isn't cached.
const purgeSlideAttributesCacheAction = (_, slideId) => {
  if (slideId == null) return
  cache.purge(slideAttributesCacheKey(slideId))
}

const getSlideAttributesByPresentationId = async (_, { presentationId, attributeKeys }) => {
  return SlideApi.getSlideAttributesByPresentationId({ presentationId, attributeKeys })
}

const toggleAcceptedAnswerViewAction = ({ state, dispatch }) => {
  dispatch('updateSlideActiveAction', {
    showAcceptedAnswerView: !state.slideActive.showAcceptedAnswerView,
  })
}

const changeIdeasVotingStepAction = async ({ state, dispatch }, { nextStep, isNext }) => {
  const currentStep = state.slideActive.votingStep
  utils.mixpanelTracking(
    EventCategories.PRESENTATION_EDITOR,
    'Brainstorm > Change voting step',
    `From step: ${currentStep} to step: ${nextStep}`,
  )
  if (isNext) {
    utils.mixpanelTracking(
      EventCategories.PRESENTATION_EDITOR,
      'Brainstorm > Change voting step > Enable all steps',
    )
  }
  dispatch('updateSlideActiveAction', { showQuestionImage: false, votingStep: nextStep })
  if (isNext && nextStep === IdeasTabName.RESULT) {
    const masonry = document.querySelector('#masonry-container')
    if (masonry && masonry.scrollTop) masonry.scrollTop = 0
  }
  dispatch('setSyncLoadingAction', 1)
  try {
    await SlideApi.patchSlideById(state.slideActive.id, {
      uniqueAccessCode: state.presentation.uniqueAccessCode,
      votingStep: nextStep,
    })
    dispatch('setSyncLoadingAction', 2)
  } catch (error) {
    dispatch('setSyncLoadingAction', 3)
    Sentry.captureException(error, `Ideas: Update votingStep to '${nextStep}' failed.`)
  }
}

export default {
  // use action which has suffix 'Action'
  updateSlideActiveAction,
  updateSlideActive: updateSlideActiveAction,
  updateSlideByIdAction,
  setSlideActiveAction,
  setSlideActive: setSlideActiveAction,
  checkSlideMovingDirectionAction,
  concatSlideOptionsAction,
  generateOptionsAction,
  updateSlideCanvasBlockByIdAction,
  flushPendingCanvasBlockAction,
  getSlideAttributesAction,
  upsertSlideAttributeAction,
  getSlideAttributesWithCacheAction,
  purgeSlideAttributesCacheAction,
  getSlideAttributesByPresentationId,
  toggleAcceptedAnswerViewAction,
  changeIdeasVotingStepAction,

  // action hook for slide events
  'SLIDE/NAVIGATION': async () => { },
}
